import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, FileText, Loader2, XCircle } from "lucide-react";
import { API, api, formatApiErrorDetail } from "@/lib/api";

export default function PayPalReturn() {
  const [params] = useSearchParams();
  const orderId = params.get("token");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    if (!orderId) return setError("Missing PayPal order reference.");
    api.post("/payments/paypal/capture", { order_id: orderId })
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [orderId]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-16" data-testid="paypal-return-page">
      <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#141419] p-8 text-center sm:p-10">
        {!data && !error && (
          <>
            <Loader2 className="mx-auto animate-spin text-emerald-300" size={40} />
            <h1 className="font-heading mt-6 text-2xl font-bold text-white">Finalizing your PayPal payment…</h1>
          </>
        )}

        {error && (
          <>
            <XCircle className="mx-auto text-red-400" size={40} />
            <h1 className="font-heading mt-6 text-2xl font-bold text-white" data-testid="paypal-error-heading">Payment not confirmed</h1>
            <p className="mt-2 text-sm text-zinc-500">{error}</p>
            <Link to="/beats" className="mt-6 inline-block rounded-full border border-white/15 px-6 py-3 text-sm text-white" data-testid="paypal-error-back-link">Back to store</Link>
          </>
        )}

        {data && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-400" size={44} />
            <p className="mt-4 font-mono text-xs uppercase tracking-widest text-emerald-300">{data.tier_name}</p>
            <h1 className="font-heading mt-2 text-3xl font-extrabold text-white" data-testid="paypal-download-title">"{data.beat_title}"</h1>
            <p className="mt-2 text-sm text-zinc-400">Paid with PayPal. Your files are ready — links were also emailed to you.</p>
            <div className="mt-8 space-y-3">
              <a href={`${API}/download/${data.download_token}`} data-testid="paypal-download-audio-button"
                className="flex items-center justify-center gap-3 rounded-full bg-emerald-300 py-4 text-sm font-bold uppercase tracking-wider text-black transition-colors hover:bg-emerald-200">
                <Download size={16} /> Download Beat (Untagged)
              </a>
              <a href={`${API}/download/${data.download_token}/license`} data-testid="paypal-download-license-button"
                className="flex items-center justify-center gap-3 rounded-full border border-emerald-300/40 py-4 text-sm font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-300/10">
                <FileText size={16} /> Download PDF License
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
